<template>
  <div class="session-history">
    <h1>Session History</h1>

    <debug-widget :data="sessions" name="sessions" :open="!sessions" />

    <div v-if="loading" class="loading">Loading sessions...</div>

    <div v-else-if="error" class="error">
      {{ error }}
    </div>

    <div v-else>
      <div v-if="sessions && sessions.length === 0" class="no-sessions">No sessions found.</div>

      <div v-else class="sessions-list">
        <div
          v-for="session in sessions"
          :key="session.id"
          class="session-card"
          @click="viewSession(session.uuid)"
        >
          <div class="session-header">
            <span class="session-status" :class="session.status">
              {{ formatStatus(session.status) }}
            </span>
            <span class="session-date">
              {{ formatDate(session.started_at || session.finished_at) }}
            </span>
          </div>

          <div class="session-details">
            <div class="countdown-time">
              {{ formatDuration(session.countdown_seconds) }}
            </div>

            <div v-if="session.starter" class="starter">
              Started by:
              <mini-user
                :online="undefined"
                :fullname="session.starter?.fullname"
                :photo_url="
                  session.starter.photo_url === undefined
                    ? NON_ANON_NULL
                    : session.starter.photo_url
                "
                :user_id="session.starter?.id"
                fallback="Unknown user"
              />
            </div>

            <div v-if="session.fail_reason" class="fail-reason">
              Fail reason: {{ formatFailReason(session.fail_reason) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button @click="goToPage(page - 1)" :disabled="page <= 1" class="page-btn">Previous</button>

        <span class="page-info"> Page {{ page }} of {{ totalPages }} </span>

        <button @click="goToPage(page + 1)" :disabled="page >= totalPages" class="page-btn">
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useApiFetch } from '../api'
import DebugWidget from '@/components/DebugWidget.vue'
import MiniUser from '@/components/MiniUser.vue'
import type { UserOut } from '@/schemas'
import { NON_ANON_NULL } from '@/stores/ws.ts'

interface SessionHistoryItem {
  id: number
  uuid: string
  status: string
  countdown_seconds: number
  starter: UserOut | null
  started_at: string | null
  finished_at: string | null
  closed_at: string | null
  fail_reason: string | null
}

interface SessionHistoryResponse {
  sessions: SessionHistoryItem[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

const router = useRouter()
const route = useRoute()
const apiFetch = useApiFetch<SessionHistoryResponse>('session history')

const sessions = ref<SessionHistoryItem[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const page = ref(1)
const totalPages = ref(1)
const perPage = 10

onMounted(() => {
  const urlPage = parseInt(route.query.page as string) || 1
  page.value = urlPage
  loadSessions()
})

watch(
  () => route.query.page,
  (newPage) => {
    page.value = parseInt(newPage as string) || 1
    loadSessions()
  },
)

async function loadSessions() {
  loading.value = true
  error.value = null

  try {
    const data = await apiFetch(`/api/me/sessions?page=${page.value}&per_page=${perPage}`)
    sessions.value = data?.sessions
    totalPages.value = data?.total_pages
  } catch (err) {
    error.value = 'Failed to load session history'
    console.error('Error loading sessions:', err)
  } finally {
    loading.value = false
  }
}

function goToPage(newPage: number) {
  if (newPage < 1 || newPage > totalPages.value) return
  router.push({ query: { ...route.query, page: newPage } })
}

function viewSession(uuid: string) {
  router.push(`/session/${uuid}`)
}

function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    created: 'Created',
    pending: 'Pending',
    ready: 'Ready',
    failsafe: 'Failsafe',
    countdown: 'Countdown',
    paused: 'Paused',
    success: 'Success',
    fail: 'Failed',
    aborted: 'Aborted',
    closed: 'Closed',
  }
  return statusMap[status] || status
}

function formatFailReason(reason: string): string {
  const reasonMap: Record<string, string> = {
    timeout: 'Time ran out',
    invalid: 'Invalid attempt',
  }
  return reasonMap[reason] || reason
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A'
  return new Date(dateStr).toLocaleDateString()
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}
</script>

<style scoped lang="scss">
.session-history {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.loading,
.error,
.no-sessions {
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
}

.error {
  color: var(--error-color);
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.session-card {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--card-bg);

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.session-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;

  &.success {
    background: var(--status-success-bg);
    color: var(--status-success);
  }

  &.fail {
    background: var(--status-fail-bg);
    color: var(--status-fail);
  }

  &.aborted {
    background: var(--status-aborted-bg);
    color: var(--status-aborted);
  }

  &.closed {
    background: var(--status-closed-bg);
    color: var(--status-closed);
  }

  &.pending {
    background: var(--status-pending-bg);
    color: var(--status-pending);
  }

  &.created {
    background: var(--status-created-bg);
    color: var(--status-created);
  }

  &.countdown {
    background: var(--status-countdown-bg);
    color: var(--status-countdown);
  }

  &.paused {
    background: var(--status-paused-bg);
    color: var(--status-paused);
  }
}

.session-date {
  color: var(--text-secondary);
  font-size: 14px;
}

.session-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.countdown-time {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.starter,
.fail-reason {
  font-size: 14px;
  color: var(--text-secondary);
}

.fail-reason {
  color: var(--error-color);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--card-bg);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--primary-color);
    background: var(--primary-color);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.page-info {
  color: var(--text-secondary);
  font-size: 14px;
}
</style>
